import React, { useMemo } from "react";
import "./RelatorioGuiaDosagemIndividualPrint.css";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";

function normalizarTexto(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function numero(valor) {
  if (valor === null || valor === undefined || valor === "") return 0;
  const convertido = Number(String(valor).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(convertido) ? convertido : 0;
}

function numeroBR(valor, casas = 0) {
  return (Number(valor) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function formatarDataBR(data) {
  if (!data) return "-";

  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }

  if (data?.toDate) return data.toDate().toLocaleDateString("pt-BR");

  return String(data);
}

function ehTipo(material, tipo) {
  return String(material?.tipo || "").toLowerCase() === tipo;
}

function nomeNormalizado(valor) {
  return normalizarTexto(valor).replace(/[^a-z0-9]+/g, " ").trim();
}

function ehAreia(material) {
  const nome = nomeNormalizado(material?.nome);
  return nome.includes("areia") && !nome.includes("po");
}

function ehPo(material) {
  const nome = nomeNormalizado(material?.nome);
  return nome === "po" || nome.includes(" po ") || nome.includes("po de pedra");
}

function materiaisVariaveisUmidade(guia) {
  const materiais = (guia.materiais || []).filter((material) =>
    String(material.nome || "").trim()
  );

  const areias = materiais.filter(ehAreia);
  const po = materiais.find(ehPo);

  return {
    areia: areias[0] || null,
    po: areias[1] || po || null,
  };
}

function ehAgua(material) {
  return ehTipo(material, "agua") || nomeNormalizado(material?.nome).includes("agua");
}

function ehAditivo(material) {
  return ehTipo(material, "aditivo");
}

function ehAgregadoConstante(material) {
  const nome = nomeNormalizado(material?.nome);

  return (
    !ehAreia(material) &&
    !ehPo(material) &&
    !ehAgua(material) &&
    !ehAditivo(material) &&
    !ehTipo(material, "cimento") &&
    !ehTipo(material, "adicao") &&
    (ehTipo(material, "agregado") || nome.includes("brita") || nome.includes("pedrisco"))
  );
}

function unidadePorTipo(tipo) {
  const tipoNormalizado = normalizarTexto(tipo);
  if (tipoNormalizado === "aditivo" || tipoNormalizado === "agua") return "l";
  return "kg";
}

function unidadeMaterial(material) {
  return unidadePorTipo(material?.tipo);
}

function guiaEhIndividual(guia) {
  return ["individual", "pesoindividual", "pesosindividuais"].includes(
    normalizarTexto(guia?.tipoGuia).replace(/\s+/g, "")
  );
}

function volumesPadrao() {
  return Array.from({ length: 15 }, (_, index) => 1 + index * 0.5);
}

function tituloTabelaPesagem(guia) {
  return guiaEhIndividual(guia)
    ? "TABELA DE PESAGEM NA CENTRAL DE CONCRETO (AGREGADOS COM PESOS INDIVIDUAIS, UM SILO PARA CADA AGREGADO)"
    : "TABELA DE PESAGEM NA CENTRAL DE CONCRETO (AGREGADOS COM PESOS ACUMULADOS, BRITA + AREIA)";
}

function colunasConstantesGuia(guia) {
  const materiais = (guia.materiais || []).filter((material) =>
    String(material.nome || "").trim()
  );

  let acumuladoAgregados = 0;

  return materiais
    .filter((material) => !ehAreia(material) && !ehPo(material) && !ehAgua(material))
    .map((material, index) => {
      const peso = numero(material.peso);
      const agregadoConstante = ehAgregadoConstante(material);

      const valorBase =
        !guiaEhIndividual(guia) && agregadoConstante
          ? (acumuladoAgregados += peso)
          : peso;

      return {
        id: `${material.nome}-${index}`,
        grupo: ehAditivo(material) ? "aditivo" : "base",
        titulo: material.nome || "-",
        unidade: unidadeMaterial(material),
        valor: (volume) =>
          numeroBR(valorBase * volume, unidadeMaterial(material) === "l" ? 1 : 0),
      };
    });
}

function linhasPesagemGuia(guia) {
  const materiais = guia.materiais || [];
  const { areia, po } = materiaisVariaveisUmidade(guia);
  const agua = materiais.find(ehAgua);

  const pesoGraudos = materiais
    .filter(ehAgregadoConstante)
    .reduce((total, material) => total + numero(material.peso), 0);

  const pesoAreia = numero(areia?.peso);
  const pesoPo = numero(po?.peso);
  const pesoAgua = numero(agua?.peso);
  const umidades = [2, 3, 4, 5, 6, 7, 8];

  return volumesPadrao().map((volume) => ({
    volume,
    umidades: umidades.map((umidade) => {
      const totalGraudos = guiaEhIndividual(guia) ? 0 : pesoGraudos * volume;
      const pesoAreiaUmida = pesoAreia * volume * (1 + umidade / 100);
      const pesoPoUmido = pesoPo * volume * (1 + umidade / 100);
      const correcaoAgua = (pesoAreia + pesoPo) * volume * (umidade / 100);

      return {
        umidade,
        areia: pesoAreia ? totalGraudos + pesoAreiaUmida : 0,
        po: pesoPo ? totalGraudos + pesoAreiaUmida + pesoPoUmido : 0,
        agua: Math.max(0, pesoAgua * volume - correcaoAgua),
      };
    }),
  }));
}

function itensResumoGuiaManual(guia) {
  return (guia.materiais || [])
    .filter((material) => String(material.nome || "").trim())
    .map((material) => ({
      titulo: material.nome || "-",
      valor: `${numeroBR(
        numero(material.peso),
        unidadeMaterial(material) === "l" ? 1 : 0
      )} ${unidadeMaterial(material)}/m³`,
      descricao: material.fornecedor || "-",
    }));
}

function RotuloMaterialUmidade({ texto }) {
  const partes = String(texto || "").trim().split(/\s+/);
  const primeiraPalavra = partes[0] || "";
  const ehAreia = normalizarTexto(primeiraPalavra) === "areia";
  const palavrasVisiveis = ehAreia ? partes.slice(0, 2) : partes;
  const restante = palavrasVisiveis.slice(1).join(" ");

  return (
    <span className="rotulo-material-umidade">
      <span>{palavrasVisiveis[0]}</span>
      {restante ? <span className="rotulo-material-umidade-complemento">{restante}</span> : null}
    </span>
  );
}

function RotuloVertical({ titulo }) {
  return (
    <span className="texto-vertical">
      <span className="texto-vertical-linha">{titulo}</span>
    </span>
  );
}

const estilosImpressaoGuiaIndividual = `
  @page {
    size: A4 landscape;
    margin: 4mm;
  }

  html,
  body {
    margin: 0;
    padding: 0;
    background: #fff;
  }

  * {
    box-sizing: border-box;
    font-family: Arial, Helvetica, sans-serif;
  }

  .relatorio-guia-individual-print {
    width: 289mm;
    min-height: 202mm;
    margin: 0 auto;
    padding: 0;
    background: #fff;
    color: #000;
    overflow: hidden;
  }

  .relatorio-guia-individual-print .acoes-print {
    display: none !important;
  }

  .relatorio-guia-individual-print .guia-pagina {
    width: 289mm;
    height: 202mm;
    min-height: 202mm;
    margin: 0;
    padding: 3mm;
    background: #fff;
    overflow: hidden;
  }

  .relatorio-guia-individual-print table {
    width: 100%;
    border-collapse: collapse;
  }

  .relatorio-guia-individual-print .guia-tabela-pesagem {
    table-layout: fixed;
  }

  .relatorio-guia-individual-print th,
  .relatorio-guia-individual-print td {
    border: 1px solid #000;
    padding: 1px 2px;
    text-align: center;
    vertical-align: middle;
    font-size: 8px;
    line-height: 1.12;
  }

  .relatorio-guia-individual-print th {
    background: #f2f2f2;
    font-weight: 700;
  }

  .relatorio-guia-individual-print .guia-cabecalho td {
    font-size: 8.4px;
    font-weight: 700;
    height: 6mm;
  }

  .relatorio-guia-individual-print .guia-logo {
    width: 34mm;
  }

  .relatorio-guia-individual-print .guia-logo img {
    max-width: 27mm;
    max-height: 11mm;
  }

  .relatorio-guia-individual-print .guia-cabecalho td.guia-titulo {
    font-size: 18.4px;
    font-weight: 700;
    line-height: 1.05;
  }

  .relatorio-guia-individual-print .guia-titulo div:first-child {
    font-size: 18.4px;
    line-height: 1.05;
  }

  .relatorio-guia-individual-print .guia-titulo div:last-child {
    font-size: 14px;
    line-height: 1.05;
  }

  .relatorio-guia-individual-print .guia-data-codigo td {
    font-size: 7.8px;
    height: 5mm;
    border-left: 0;
    border-right: 0;
    text-align: left;
  }

  .relatorio-guia-individual-print .guia-data-codigo td:last-child {
    text-align: right;
  }

  .relatorio-guia-individual-print .guia-empresa {
    font-size: 8px;
    line-height: 1.25;
    margin: 2.5px 0;
  }

  .relatorio-guia-individual-print .guia-faixa {
    background: #4472c4;
    color: #fff;
    text-align: center;
    font-weight: 700;
    font-size: 8.3px;
    padding: 2px;
    margin-top: 2.5px;
  }

  .relatorio-guia-individual-print .guia-resumo {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    border: 1px solid #000;
    border-top: 0;
  }

  .relatorio-guia-individual-print .guia-resumo span {
    border-right: 1px solid #000;
    padding: 2px;
    font-size: 8px;
    text-align: center;
  }

  .relatorio-guia-individual-print .guia-resumo span:last-child {
    border-right: 0;
  }

  .relatorio-guia-individual-print .guia-subtitulo {
    text-align: center;
    font-weight: 700;
    font-size: 8px;
    border: 1px solid #000;
    border-top: 0;
    padding: 2px;
    background: #fff;
  }

  .relatorio-guia-individual-print .guia-dados-traco-grid {
    border-left: 1px solid #000;
    border-right: 1px solid #000;
  }

  .relatorio-guia-individual-print .guia-dados-traco-linha {
    display: grid;
  }

  .relatorio-guia-individual-print .guia-dados-traco-card {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    border-bottom: 1px solid #000;
    border-right: 1px solid #000;
    min-height: 10.5mm;
  }

  .relatorio-guia-individual-print .guia-dados-traco-card:last-child {
    border-right: 0;
  }

  .relatorio-guia-individual-print .guia-card-esquerda,
  .relatorio-guia-individual-print .guia-card-direita {
    padding: 2px;
    font-size: 7.6px;
    line-height: 1.15;
  }

  .relatorio-guia-individual-print .guia-card-esquerda {
    border-right: 0;
  }

  .relatorio-guia-individual-print .guia-card-esquerda strong,
  .relatorio-guia-individual-print .guia-card-direita strong,
  .relatorio-guia-individual-print .guia-card-esquerda span,
  .relatorio-guia-individual-print .guia-card-direita span {
    display: block;
  }

  .relatorio-guia-individual-print .guia-card-esquerda span {
    align-items: center;
    color: #0000ff;
    display: flex;
    font-size: 11.6px;
    font-weight: 700;
    justify-content: center;
    min-height: 5.5mm;
    text-align: center;
  }

  .relatorio-guia-individual-print .guia-tabela-pesagem th {
    font-size: 8.2px;
    font-weight: 700;
    height: 5.8mm;
    background: #f2f2f2;
  }

  .relatorio-guia-individual-print .rotulo-material-umidade {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .relatorio-guia-individual-print .rotulo-material-umidade-complemento {
    font-size: 6.4px;
    line-height: 1;
  }

  .relatorio-guia-individual-print .guia-tabela-pesagem td {
    font-size: 9.2px;
    font-weight: 700;
    line-height: 1.08;
    height: 6.4mm;
  }

  .relatorio-guia-individual-print .guia-tabela-pesagem tbody tr:nth-child(even) td {
    background: #dbeef3;
  }

  .relatorio-guia-individual-print .guia-tabela-pesagem tbody tr:nth-child(odd) td {
    background: #fff;
  }

  .relatorio-guia-individual-print .texto-vertical {
    display: block;
    font-size: 6.6px;
    left: 50%;
    line-height: 1.02;
    overflow-wrap: anywhere;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%) rotate(-90deg);
    transform-origin: center;
    white-space: normal;
    width: 16mm;
    writing-mode: horizontal-tb;
  }

  .relatorio-guia-individual-print .texto-vertical-linha {
    display: block;
  }

  .relatorio-guia-individual-print .volume-title {
    overflow: hidden;
    position: relative;
    width: 7mm;
  }

  .relatorio-guia-individual-print .volume-title .texto-vertical {
    font-size: 7.2px;
    width: 22mm;
  }

  .relatorio-guia-individual-print .constante-vertical {
    overflow: hidden;
    position: relative;
    width: 7mm;
  }

  .relatorio-guia-individual-print .constante-aditivo .texto-vertical {
    font-size: 5.5px;
    line-height: 0.98;
    width: 11mm;
  }

  .relatorio-guia-individual-print .volume {
    font-weight: 700;
    color: #0000ff;
  }

  .relatorio-guia-individual-print .agua {
    color: #ff0000;
    font-weight: 700;
  }

  .relatorio-guia-individual-print .guia-obs {
    font-size: 7.6px;
    line-height: 1.2;
    margin-top: 2.5px;
  }

  .relatorio-guia-individual-print * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`;

function imprimirRelatorio() {
  const conteudo = document.querySelector(".relatorio-guia-individual-print");
  if (!conteudo) return;

  const janela = window.open("", "_blank", "width=1200,height=800");

  if (!janela) {
    alert("Permita pop-ups para imprimir a guia de dosagem.");
    return;
  }

  janela.document.write(`
    <html>
      <head>
        <title>Guia de Dosagem</title>
        <base href="${window.location.origin}/" />
        <style>${estilosImpressaoGuiaIndividual}</style>
      </head>
      <body>
        ${conteudo.outerHTML}
        <script>
          window.onload = async function () {
            const imagens = Array.from(document.images);
            await Promise.all(imagens.map(function (imagem) {
              if (imagem.complete) return Promise.resolve();
              return new Promise(function (resolve) {
                imagem.onload = resolve;
                imagem.onerror = resolve;
              });
            }));
            window.focus();
            window.print();
            window.close();
          };
        </script>
      </body>
    </html>
  `);

  janela.document.close();
}
export default function RelatorioGuiaDosagemIndividualPrint({ guia, onVoltar }) {
  const constantes = useMemo(() => colunasConstantesGuia(guia), [guia]);
  const colunasBase = constantes.filter((coluna) => coluna.grupo !== "aditivo");
  const colunasAditivos = constantes.filter((coluna) => coluna.grupo === "aditivo");
  const linhas = useMemo(() => linhasPesagemGuia(guia), [guia]);
  const itensResumo = useMemo(() => itensResumoGuiaManual(guia), [guia]);
  const materiaisUmidade = useMemo(() => materiaisVariaveisUmidade(guia), [guia]);
  const numeroTraco = guia.traco || "-";
  useNomeArquivoPdf(numeroTraco);
  const rotuloAreia = materiaisUmidade.areia?.nome || "AREIA";
  const rotuloPo = materiaisUmidade.po?.nome || "PÓ";

  const linhasResumo = useMemo(() => {
    const totalLinhas = Math.max(1, Math.ceil(itensResumo.length / 6));
    const itensPorLinha = Math.ceil(itensResumo.length / totalLinhas);

    return Array.from({ length: totalLinhas }, (_, indice) =>
      itensResumo.slice(indice * itensPorLinha, (indice + 1) * itensPorLinha)
    ).filter((linha) => linha.length);
  }, [itensResumo]);

  return (
    <div className="relatorio-guia-individual-print">
      <div className="acoes-print">
        <button className="btn-voltar" onClick={onVoltar}>
          Voltar
        </button>
        <button className="btn-imprimir" onClick={imprimirRelatorio}>
          Imprimir
        </button>
      </div>

      <div className="guia-pagina">
        <table className="guia-cabecalho">
          <tbody>
            <tr>
              <td rowSpan="3" className="guia-logo">
                <LogoRelatorio alt="Logo"  />
              </td>
              <td rowSpan="3" className="guia-titulo">
                <div>SISTEMA DE GESTÃO DA QUALIDADE</div>
                <div>Concreto - Laudo de dosagem - Carta traço</div>
              </td>
              <td>RQ 11.1</td>
            </tr>
            <tr>
              <td>REVISÃO 00</td>
            </tr>
            <tr>
              <td>20/08/2025</td>
            </tr>
          </tbody>
        </table>

        <table className="guia-data-codigo">
          <tbody>
            <tr>
              <td>
                DATA: <strong>{formatarDataBR(guia.data)}</strong>
              </td>
              <td>
                CÓDIGO: <strong>{numeroTraco}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="guia-empresa">
          <div>
            <strong>EMPRESA:</strong> {guia.clienteNome || "-"}
          </div>
          <div>
            <strong>OBRA:</strong> {guia.obraNome || "-"}
          </div>
        </div>

        <div className="guia-faixa">GUIA DE DOSAGEM DE CONCRETO</div>

        <div className="guia-resumo">
          <span>
            TRAÇO Nº: <strong>{numeroTraco}</strong>
          </span>
          <span>
            FCK: <strong>{guia.fck || "-"} MPa</strong>
          </span>
          <span>
            FATOR A/C: <strong>{guia.fatorAc || "-"}</strong>
          </span>
          <span>
            SLUMP: <strong>{guia.slump || "-"}</strong>
          </span>
          <span>
            DATA EXEC.: <strong>{formatarDataBR(guia.data)}</strong>
          </span>
        </div>

        <div className="guia-subtitulo">DADOS DO TRAÇO - PARA CÁLCULO DA TABELA</div>

        <div className="guia-dados-traco-grid">
          {linhasResumo.map((linha, indiceLinha) => (
            <div
              className="guia-dados-traco-linha"
              key={`linha-${indiceLinha}`}
              style={{ gridTemplateColumns: `repeat(${linha.length}, minmax(0, 1fr))` }}
            >
              {linha.map((item, index) => (
                <div
                  className="guia-dados-traco-card"
                  key={`${item.titulo}-${indiceLinha}-${index}`}
                >
                  <div className="guia-card-esquerda">
                    <strong>{item.titulo}</strong>
                    <span>{item.valor}</span>
                  </div>
                  <div className="guia-card-direita">
                    <strong>{item.descricao || "-"}</strong>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="guia-subtitulo">{tituloTabelaPesagem(guia)}</div>

        <table className="guia-tabela-pesagem">
          <colgroup>
            <col className="col-volume" />
            {constantes.map((coluna) => (
              <col className="col-constante" key={`col-${coluna.id}`} />
            ))}
            {Array.from({ length: 21 }, (_, index) => (
              <col className="col-variavel" key={`col-var-${index}`} />
            ))}
          </colgroup>

          <thead>
            <tr>
              <th rowSpan="4" className="volume-title">
                <span className="texto-vertical">VOLUME A DOSAR</span>
              </th>
              <th colSpan={Math.max(1, constantes.length)}>QUANT. CONSTANTES</th>
              <th colSpan="21">QUANTIDADES VARIÁVEIS COM A UMIDADE</th>
            </tr>

            <tr>
              {colunasBase.map((coluna) => (
                <th rowSpan="3" className="constante-vertical" key={coluna.id}>
                  <RotuloVertical titulo={coluna.titulo} />
                </th>
              ))}

              {colunasAditivos.length ? (
                <th colSpan={colunasAditivos.length}>ADITIVO</th>
              ) : null}

              <th colSpan="21">UMIDADE DAS AREIAS</th>
            </tr>

            <tr>
              {colunasAditivos.map((coluna) => (
                <th rowSpan="2" className="constante-vertical constante-aditivo" key={coluna.id}>
                  <RotuloVertical titulo={coluna.titulo} />
                </th>
              ))}

              {[2, 3, 4, 5, 6, 7, 8].map((umidade) => (
                <th colSpan="3" key={umidade}>
                  {umidade}%
                </th>
              ))}
            </tr>

            <tr>
              {[2, 3, 4, 5, 6, 7, 8].flatMap((umidade) => [
                <th key={`${umidade}-areia`}><RotuloMaterialUmidade texto={rotuloAreia} /></th>,
                <th key={`${umidade}-po`}><RotuloMaterialUmidade texto={rotuloPo} /></th>,
                <th key={`${umidade}-agua`}>ÁGUA</th>,
              ])}
            </tr>
          </thead>

          <tbody>
            {linhas.map((linha) => (
              <tr key={linha.volume}>
                <td className="volume">{numeroBR(linha.volume, 1)}</td>

                {constantes.map((coluna) => (
                  <td key={`${linha.volume}-${coluna.id}`}>
                    {coluna.valor(linha.volume)}
                  </td>
                ))}

                {linha.umidades.flatMap((umidade) => [
                  <td key={`${linha.volume}-${umidade.umidade}-areia`}>
                    {umidade.areia ? numeroBR(umidade.areia, 0) : "-"}
                  </td>,
                  <td key={`${linha.volume}-${umidade.umidade}-po`}>
                    {umidade.po ? numeroBR(umidade.po, 0) : "-"}
                  </td>,
                  <td className="agua" key={`${linha.volume}-${umidade.umidade}-agua`}>
                    {numeroBR(umidade.agua, 0)}
                  </td>,
                ])}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="guia-obs">
          Obs.: Atentar para a umidade correta da areia. Recomenda-se a determinação
          sempre no início dos trabalhos ou quando houver variação devido à chegada de
          material ou por chuva.
          <br />
          {guiaEhIndividual(guia)
            ? "As massas dos agregados estão individuais, considerando um silo para cada agregado. Não foi considerada umidade para a brita."
            : "As massas das areias e britas estão acumuladas. Não foi considerada umidade para a brita."}
        </div>
      </div>
    </div>
  );
}
